import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Skeleton,
  Chip,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGetDealerLocationDistributionQuery } from '../../../app/services/dealer';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from 'recharts';

const DealerLocationDistributionCard: React.FC = () => {
  const navigate = useNavigate();
  const { data: distribution, isLoading, isError } = useGetDealerLocationDistributionQuery();

  const handleViewDealers = (locationCount: number) => {
    // Navigate to dealers page with location count filter
    navigate(`/dealers?locationCount=${locationCount}`);
  };

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Dealers by Location Count</Typography>
        </Box>
        <Skeleton variant="rectangular" height={200} />
      </Paper>
    );
  }

  if (isError || !distribution) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Typography color="error">Failed to load distribution data</Typography>
      </Paper>
    );
  }

  const totalDealers = distribution.reduce((sum, item) => sum + item.dealerCount, 0);
  const totalLocations = distribution.reduce((sum, item) => sum + (item.dealerCount * item.locationCount), 0);

  // Prepare chart data with colors and percentages
  const chartData = distribution.map((item) => ({
    locationCount: item.locationCount,
    dealerCount: item.dealerCount,
    testDealerCount: item.testDealerCount,
    label: `${item.locationCount}`, // Just the number for X-axis
    percentage: ((item.dealerCount / totalDealers) * 100).toFixed(1),
  }));

  // Color palette for bars
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#d084d0', '#a4de6c'];

  // Custom tooltip to show percentage
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const locationLabel = `${data.locationCount} ${data.locationCount === 1 ? 'Location' : 'Locations'}`;
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, boxShadow: 2 }}>
          <Typography variant="body2" fontWeight="bold">{locationLabel}</Typography>
          <Typography variant="body2" color="primary">{data.dealerCount} dealers ({data.percentage}%)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {data.dealerCount - data.testDealerCount} live · {data.testDealerCount} test
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Click to view these dealers
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Handle bar click - this receives the entire data point
  const handleBarClick = (data: any, index: number) => {
    if (data && data.locationCount) {
      handleViewDealers(data.locationCount);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Dealers by Location Count</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label={`${totalDealers} Unique Dealers`} size="small" color="primary" variant="outlined" />
          <Tooltip title="This counts unique parent dealers (by DMS Dealer ID). The total includes all dealer locations.">
            <Chip 
              icon={<InfoOutlinedIcon fontSize="small" />}
              label={`${totalLocations} Total Locations`} 
              size="small" 
              color="default" 
              variant="outlined" 
            />
          </Tooltip>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> This chart shows {totalDealers} unique parent dealers (where DealerId = CompanyDealerId) 
          grouped by their location count. These dealers collectively operate {totalLocations} total locations. 
          This matches the "Key Metrics" dealer count which also counts parent dealers only.
        </Typography>
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Click on any bar to view dealers with that location count
      </Typography>

      {/* Interactive Bar Chart */}
      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            margin={{ top: 10, right: 10, left: 0, bottom: 70 }}
            barCategoryGap="20%"
          >
            <XAxis 
              dataKey="label" 
              tick={{ fontSize: 12 }}
              label={{ value: 'Number of Locations', position: 'insideBottom', offset: -10, style: { fontSize: 13, fontWeight: 'bold' } }}
            />
            <YAxis 
              tick={{ fontSize: 12 }} 
              label={{ value: 'Number of Dealers', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} 
            />
            <RTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }} />
            <Bar 
              dataKey="dealerCount" 
              radius={[8, 8, 0, 0]}
              onClick={handleBarClick}
              minPointSize={5}
              barSize={60}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index % COLORS.length]}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default DealerLocationDistributionCard;
